import React, {useEffect, useRef, useState} from 'react';
import {App, Button, Card, Checkbox, Divider, Modal, Select, Space, Tag, Tooltip, Typography} from 'antd';
import {
    DownOutlined,
    ExperimentOutlined,
    KeyOutlined,
    PlayCircleOutlined,
    RobotOutlined,
    SecurityScanOutlined,
    ThunderboltOutlined,
    UpOutlined
} from '@ant-design/icons';
import {apiConnectionService} from '../../services/apiConnectionService';
import {ApiConnectionModel} from '../../types/apiConnection';
import {userAiConfigService} from '../user-ai-config/userAiConfigService';
import {RiskMode, RiskModeMap, TradeType, TradingStyle, TradingStyleMap} from '../user-ai-config/types';
import {useAuth} from '../../contexts/AuthContext';
import {agentConfigService} from '../../services/agentConfigService';
import {AgentOption, isAgentDisabledForTrigger} from './botControlUtils';

const {Title, Text} = Typography;
const {Option} = Select;

// 测试模式首期支持的品种
const TEST_SYMBOLS = [
    'BTC-USDT-SWAP',
    'ETH-USDT-SWAP',
    'SOL-USDT-SWAP',
    'SUI-USDT-SWAP',
    'XRP-USDT-SWAP',
    'ADA-USDT-SWAP',
];

const TEST_TRADE_ACTIONS = ['BUY', 'SELL', 'BUY_SELL'] as const;

interface BotStatusType {
    callCount: number;
    lastCallTime: number | null;
    modelName: string;
    apiKeyId: number;
    apiKeyDescription: string;
    riskControlMode: string;
    tradingStyle: string;
    automaticTradeEnabled: boolean;
    systemStatus: string;
    lastProcessingTime: number;
}

interface BotControlProps {
    selectedApiKeyId: number | null;
    onTriggerBot: (apiKeyId: number, agentId?: number, isTestMode?: boolean,
                   testAction?: string, testSymbol?: string) => void;
    onApiKeyChange: (apiKeyId: number) => void;
    onGeneratePrompt?: (apiKeyId: number, agentId?: number) => void;
    refreshTrigger: number;
    botStatus: BotStatusType | null;
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
    generatingPrompt?: boolean;
    publicView?: boolean;
    selectedAgentId?: number | null;
    onAgentChange?: (agentId: number | null) => void;
}

const BotControl: React.FC<BotControlProps> = ({
                                                   selectedApiKeyId,
                                                   onTriggerBot,
                                                   onApiKeyChange,
                                                   onGeneratePrompt,
                                                   refreshTrigger,
                                                   botStatus,
                                                   collapsed,
                                                   setCollapsed,
                                                   generatingPrompt,
                                                   publicView,
                                                   selectedAgentId,
                                                   onAgentChange
                                               }) => {
    const {message: messageApi} = App.useApp();
    const [connections, setConnections] = useState<ApiConnectionModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [userAiConfig, setUserAiConfig] = useState<{
        riskMode: RiskMode;
        tradingStyle: TradingStyle;
        tradeType: TradeType;
    } | null>(null);

    // 倒计时状态管理: 用于BOT触发后的30秒冷却
    const [countdown, setCountdown] = useState(0); // 倒计时剩余秒数,0表示未启动
    const countdownTimerRef = useRef<number | null>(null); // 定时器引用,用于清理

    // 测试模式状态（仅 admin 可见）
    const [testMode, setTestMode] = useState(false);
    const [testAction, setTestAction] = useState('BUY');
    const [testSymbol, setTestSymbol] = useState('BTC-USDT-SWAP');
    // 测试模式控件行显示状态（默认隐藏，通过左侧图标展开）
    const [testPanelVisible, setTestPanelVisible] = useState(false);

    // 管理员权限判断 & 智能体列表
    const {hasPermission, user} = useAuth();
    const isAdmin = hasPermission('SYSTEM_AGENT_CONFIGS');
    // 测试模式相关控件严格限定 ADMIN 角色（区别于 hasPermission：非ADMIN角色即使拥有配置权限也不可见）
    const isAdminRole = user?.roles?.includes('ADMIN') ?? false;
    const [agents, setAgents] = useState<AgentOption[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(false);

    // 交易风格映射
    const getTradingStyleConfig = (style: string) => {
        const styleMap: Record<string, { text: string; color: string; style?: React.CSSProperties }> = {
            '保守型': {
                text: '保守型',
                color: 'rgba(82, 196, 26, 0.3)', // Green
                style: {color: 'var(--pt-bull)', border: '1px solid var(--pt-bull)'}
            },
            '谨慎型': {
                text: '谨慎型',
                color: 'rgba(160, 217, 17, 0.3)', // Lime
                style: {color: '#a0d911', border: '1px solid #a0d911'}
            },
            '稳健型': {
                text: '稳健型',
                color: 'rgba(250, 173, 20, 0.3)', // Gold/Yellow
                style: {color: 'var(--pt-amber)', border: '1px solid var(--pt-amber)'}
            },
            '积极型': {
                text: '积极型',
                color: 'rgba(250, 140, 22, 0.3)', // Orange
                style: {color: 'var(--pt-amber)', border: '1px solid var(--pt-amber)'}
            },
            '激进型': {
                text: '激进型',
                color: 'rgba(255, 77, 79, 0.3)', // Red
                style: {color: 'var(--pt-bear)', border: '1px solid var(--pt-bear)'}
            }
        };
        return styleMap[style] || {text: style, color: 'default'};
    };

    // 风控模式映射
    const getRiskModeConfig = (mode: string) => {
        const modeMap: Record<string, { text: string; color: string; style?: React.CSSProperties }> = {
            'AUTO': {
                text: '自动模式',
                color: 'rgba(250, 173, 20, 0.3)', // 橙色透明
                style: {color: 'var(--pt-amber)', border: '1px solid var(--pt-amber)'}
            },
            'MANUAL': {
                text: '人工模式',
                color: 'rgba(82, 196, 26, 0.3)', // 绿色透明
                style: {color: 'var(--pt-bull)', border: '1px solid var(--pt-bull)'}
            },
            '自动模式': {
                text: '自动模式',
                color: 'rgba(250, 173, 20, 0.3)',
                style: {color: 'var(--pt-amber)', border: '1px solid var(--pt-amber)'}
            }, // 兼容旧数据
            '手动模式': {
                text: '人工模式',
                color: 'rgba(82, 196, 26, 0.3)',
                style: {color: 'var(--pt-bull)', border: '1px solid var(--pt-bull)'}
            }  // 兼容旧数据
        };
        return modeMap[mode] || {text: mode, color: 'default'};
    };

    // 获取API连接列表
    const fetchConnections = async () => {
        try {
            setLoading(true);
            const data = await apiConnectionService.getAllConnections();
            setConnections(data);

            // 如果没有选中，设置默认连接
            if (!selectedApiKeyId && data.length > 0) {
                const defaultConn = data.find(c => !!c.isDefault);
                const firstId = data[0].id;
                if (firstId != null) {
                    onApiKeyChange(defaultConn?.id ?? firstId);
                }
            }
        } catch (error) {
            // console.error('获取API连接列表失败:', error);
            messageApi.error('获取API连接列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 处理生成prompt
    const handleGeneratePrompt = () => {
        if (!selectedApiKeyId) {
            messageApi.warning('请先选择API Key');
            return;
        }

        if (onGeneratePrompt) {
            onGeneratePrompt(selectedApiKeyId, selectedAgentId ?? undefined);
        }
    };

    // 启动倒计时函数: 在BOT触发成功后启动30秒冷却倒计时
    const startCountdown = () => {
        // 清除之前的定时器,防止重复启动
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
        }

        // 初始化倒计时为30秒
        setCountdown(30);

        // 启动定时器,每秒递减
        countdownTimerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    // 倒计时结束,清理定时器
                    if (countdownTimerRef.current) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // 处理BOT触发（保留原有功能）
    const handleTrigger = async () => {
        if (!selectedApiKeyId) {
            messageApi.warning('请先选择API Key');
            return;
        }

        // 智能体禁用前置拦截（仅管理员显式指定了智能体时，前端可判断其禁用状态）。
        // 非管理员使用默认交易智能体，其禁用状态由后端在 AgentExecutionWrapper 统一拦截兜底。
        if (isAgentDisabledForTrigger(selectedAgentId, agents)) {
            messageApi.warning('所选智能体已被禁用，无法执行');
            return;
        }

        // 测试模式确认弹窗（真实下单/查询分析提示）
        if (testMode) {
            const isTradeAction = TEST_TRADE_ACTIONS.includes(testAction as typeof TEST_TRADE_ACTIONS[number]);
            const confirmContent = isTradeAction
                ? testAction === 'BUY_SELL'
                    ? `测试模式将强制 Agent 对 ${testSymbol} 做出开仓决策并真实下单（方向由模型在 BUY/SELL 中自行判断）。请确认账户无持仓或使用测试账户。`
                    : `测试模式将强制 Agent 对 ${testSymbol} 做出 ${testAction} 开仓决策并真实下单（消耗资金）。请确认账户无持仓或使用测试账户。`
                : `测试模式将强制 Agent 查询 ${testSymbol} 的市场数据并输出分析结论，不进行任何交易。`;
            const confirmed = await new Promise<boolean>(resolve => {
                Modal.confirm({
                    title: <span style={{color: '#ffffff'}}>测试模式确认</span>,
                    className: 'bot-confirm-modal',
                    centered: true,
                    width: 560,
                    content: <div style={{color: '#d9d9d9'}}>{confirmContent}</div>,
                    okText: '确认测试',
                    cancelText: '取消',
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
            if (!confirmed) {
                return;
            }
        }

        try {
            setTriggering(true);
            await onTriggerBot(selectedApiKeyId, selectedAgentId ?? undefined,
                testMode ? true : undefined, testMode ? testAction : undefined,
                testMode ? (testAction === 'QUERY' ? undefined : testSymbol) : undefined);

            // 接口调用成功后启动30秒倒计时
            startCountdown();
        } catch (error) {
            // console.error('触发BOT失败:', error);
        } finally {
            setTriggering(false);
        }
    };

    // API Key切换
    const handleApiKeyChange = (apiKeyId: number) => {
        onApiKeyChange(apiKeyId);
    };

    // 初始化和刷新时加载数据
    useEffect(() => {
        if (publicView) {
            return;
        }
        fetchConnections();
        fetchUserAiConfig();
    }, [refreshTrigger, publicView]);

    // 获取智能体列表（仅管理员）
    useEffect(() => {
        if (isAdmin) {
            setAgentsLoading(true);
            agentConfigService.getActiveAgentConfigs()
                .then(data => {
                    setAgents(data.map((a: any) => ({id: a.id, name: a.name, isActive: a.isActive})));
                })
                .catch(() => {
                    // 静默失败
                })
                .finally(() => setAgentsLoading(false));
        }
    }, [isAdmin]);

    // 组件卸载时清理定时器,防止内存泄漏
    useEffect(() => {
        return () => {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        };
    }, []);

    // 获取用户AI交易配置
    const fetchUserAiConfig = async () => {
        try {
            const config = await userAiConfigService.getConfig();
            setUserAiConfig({
                riskMode: config.riskMode,
                tradingStyle: config.tradingStyle,
                tradeType: config.tradeType
            });
        } catch (error) {
            // console.error('获取用户AI交易配置失败:', error);
        }
    };

    return (
        <Card
            title={
                <Space>
                    <RobotOutlined style={{color: 'var(--pt-primary)'}}/>
                    <span>Agent控制面板</span>
                </Space>
            }
            extra={
                <Space size={4}>
                    {/* 测试模式控件显隐开关：位于控制面板展开/收起按钮左侧 */}
                    {isAdminRole && (
                        <Tooltip title={testPanelVisible ? '收起测试模式控件' : '展开测试模式控件'}>
                            <Button
                                type="text"
                                aria-pressed={testPanelVisible}
                                icon={<ExperimentOutlined/>}
                                onClick={() => setTestPanelVisible(v => !v)}
                                style={{
                                    color: testPanelVisible ? 'var(--pt-primary)' : 'rgba(255,255,255,0.45)',
                                    background: testPanelVisible ? 'rgba(240,180,41,0.12)' : 'transparent',
                                    borderRadius: 8
                                }}
                            />
                        </Tooltip>
                    )}
                    <Button
                        type="text"
                        icon={collapsed ? <DownOutlined/> : <UpOutlined/>}
                        onClick={() => setCollapsed(!collapsed)}
                        style={{color: collapsed ? 'rgba(255, 255, 255, 0.65)' : 'var(--pt-primary)'}}
                    />
                </Space>
            }
            style={{
                backgroundColor: 'var(--pt-bg-panel)',
                border: '1px solid var(--pt-border)',
                borderRadius: '8px',
                maxWidth: '100%',  // 确保不超出父容器
                overflow: 'hidden'  // 防止内容溢出
            }}
            styles={{
                header: {
                    backgroundColor: '#1a1a1a',
                    borderBottom: '1px solid var(--pt-border)',
                    color: 'rgba(255, 255, 255, 0.95)'
                },
                body: {
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto'  // 允许内容滚动,防止溢出
                }
            }}
        >
            <div style={{display: 'flex', flexDirection: 'column'}}>
                {/* 上部分 - 配置信息 */}
                {!collapsed && (
                    <div style={{flex: '0 0 auto', marginBottom: '8px'}}>
                        <Space direction="vertical" size="small" style={{width: '100%'}}>
                            {/* API Key选择 */}
                            <div>
                                <Space align="center" style={{width: '100%', justifyContent: 'space-between'}}>
                                    <Space>
                                        <KeyOutlined style={{color: 'var(--pt-primary)'}}/>
                                        <Text strong style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: '12px'}}>
                                            API Key
                                        </Text>
                                    </Space>
                                    {publicView ? (
                                        <Text style={{color: 'rgba(255, 255, 255, 0.85)', fontSize: '12px'}}>
                                            {botStatus?.apiKeyDescription || botStatus?.apiKeyId || '-'}
                                        </Text>
                                    ) : (
                                        <Select
                                            value={selectedApiKeyId}
                                            onChange={handleApiKeyChange}
                                            placeholder="选择API连接"
                                            loading={loading}
                                            style={{width: '180px', fontSize: '12px'}}
                                            size="small"
                                        >
                                            {connections.map(conn => (
                                                <Option key={conn.id} value={conn.id}>
                                                    <Space>
                                                        <span style={{fontSize: '12px'}}>{conn.name}</span>
                                                        {conn.liveTrading === 1 && (
                                                            <Tag color="red"
                                                                 style={{
                                                                     fontSize: '10px',
                                                                     lineHeight: '18px'
                                                                 }}>实盘</Tag>
                                                        )}
                                                        <Tag color={conn.status === 'active' ? 'success' : 'default'}
                                                             style={{fontSize: '10px', lineHeight: '18px'}}>
                                                            {conn.status}
                                                        </Tag>
                                                    </Space>
                                                </Option>
                                            ))}
                                        </Select>
                                    )}
                                </Space>
                            </div>

                            {/* 智能体选择 - 仅管理员可见 */}
                            {isAdmin && (
                                <div>
                                    <Space align="center" style={{width: '100%', justifyContent: 'space-between'}}>
                                        <Space>
                                            <RobotOutlined style={{color: '#8B5CF6'}}/>
                                            <Text strong style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: '12px'}}>
                                                智能体
                                            </Text>
                                        </Space>
                                        <Select
                                            value={selectedAgentId}
                                            onChange={(value) => onAgentChange?.(value ?? null)}
                                            placeholder="默认智能体"
                                            allowClear
                                            loading={agentsLoading}
                                            style={{width: '180px', fontSize: '12px'}}
                                            size="small"
                                        >
                                            {agents.map(agent => (
                                                <Option key={agent.id} value={agent.id}>
                                                    <span style={{fontSize: '12px'}}>{agent.name}</span>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Space>
                                </div>
                            )}

                            {/* 风控模式和交易风格展示 */}
                            <div>
                                <Space align="center" style={{width: '100%', justifyContent: 'space-between'}}>
                                    <Space>
                                        <SecurityScanOutlined style={{color: 'var(--pt-amber)'}}/>
                                        <Text strong style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: '12px'}}>
                                            风控模式
                                        </Text>
                                    </Space>
                                    {userAiConfig ? (
                                        <Tag style={{
                                            fontSize: '12px',
                                            padding: '2px 8px',
                                            backgroundColor: 'rgba(250, 173, 20, 0.3)',
                                            color: 'var(--pt-amber)',
                                            border: '1px solid var(--pt-amber)'
                                        }}>
                                            {RiskModeMap[userAiConfig.riskMode]}
                                        </Tag>
                                    ) : (publicView && botStatus?.riskControlMode) ? (
                                        <Tag style={{
                                            fontSize: '12px',
                                            padding: '2px 8px',
                                            ...getRiskModeConfig(botStatus.riskControlMode).style
                                        }}>
                                            {getRiskModeConfig(botStatus.riskControlMode).text}
                                        </Tag>
                                    ) : null}
                                </Space>
                            </div>

                            <div>
                                <Space align="center" style={{width: '100%', justifyContent: 'space-between'}}>
                                    <Space>
                                        <ThunderboltOutlined style={{color: 'var(--pt-bull)'}}/>
                                        <Text strong style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: '12px'}}>
                                            交易风格
                                        </Text>
                                    </Space>
                                    {userAiConfig ? (
                                        <Tag style={{
                                            fontSize: '12px',
                                            padding: '2px 8px',
                                            backgroundColor: 'rgba(250, 173, 20, 0.3)',
                                            color: 'var(--pt-amber)',
                                            border: '1px solid var(--pt-amber)'
                                        }}>
                                            {TradingStyleMap[userAiConfig.tradingStyle].label}
                                        </Tag>
                                    ) : (publicView && botStatus?.tradingStyle) ? (
                                        <Tag style={{
                                            fontSize: '12px',
                                            padding: '2px 8px',
                                            ...getTradingStyleConfig(botStatus.tradingStyle).style
                                        }}>
                                            {getTradingStyleConfig(botStatus.tradingStyle).text}
                                        </Tag>
                                    ) : null}
                                </Space>
                            </div>

                            <Divider style={{margin: '8px 0', borderColor: 'var(--pt-border)'}}/>
                        </Space>
                    </div>
                )}

                {/* 下部分 - Agent执行按钮 */}
                <div style={{
                    flex: '0 0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '8px 0',
                    width: '100%'
                }}>
                    {/* 测试模式控件行（默认隐藏，位于按钮上方） */}
                    {isAdminRole && testPanelVisible && (
                        <div style={{marginBottom: 8}}>
                            <Space size={8} align="center">
                                <Checkbox
                                    checked={testMode}
                                    onChange={e => setTestMode(e.target.checked)}
                                    style={{color: 'rgba(255,255,255,0.75)', fontSize: '12px'}}
                                >
                                    测试模式
                                </Checkbox>
                                {testMode && (
                                    <>
                                        <Select
                                            size="small"
                                            value={testAction}
                                            onChange={setTestAction}
                                            style={{width: 116}}
                                            options={[
                                                {value: 'BUY', label: 'BUY'},
                                                {value: 'SELL', label: 'SELL'},
                                                {value: 'BUY_SELL', label: 'BUY/SELL'},
                                                {value: 'QUERY', label: 'QUERY'},
                                            ]}
                                        />
                                        {TEST_TRADE_ACTIONS.includes(testAction as typeof TEST_TRADE_ACTIONS[number]) && (
                                            <Select
                                                size="small"
                                                value={testSymbol}
                                                onChange={setTestSymbol}
                                                style={{width: 160}}
                                                placeholder="选择品种"
                                                options={TEST_SYMBOLS.map(s => ({value: s, label: s}))}
                                            />
                                        )}
                                    </>
                                )}
                            </Space>
                        </div>
                    )}

                    <Space direction="horizontal" size="middle" align="center"
                           style={{width: '100%', justifyContent: 'center'}}>
                        {/* 生成Prompt按钮（折叠区域内 - 调试/高级用途） */}
                        {!collapsed && (
                            <Button
                                type="primary"
                                size="large"
                                icon={<PlayCircleOutlined/>}
                                onClick={handleGeneratePrompt}
                                disabled={!selectedApiKeyId || connections.length === 0}
                                loading={generatingPrompt}
                                style={{
                                    height: '46px',
                                    width: '170px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, var(--pt-bull) 0%, #73d13d 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)'
                                }}
                            >
                                生成Prompt
                            </Button>
                        )}

                        {/* Agent智能执行按钮 */}
                        <Button
                            type="default"
                            size="large"
                            icon={<RobotOutlined/>}
                            onClick={handleTrigger}
                            loading={triggering}
                            disabled={!selectedApiKeyId || connections.length === 0 || countdown > 0}
                            style={{
                                height: '46px',
                                width: collapsed ? '220px' : '170px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #F0B429 0%, #f7c948 100%)',
                                border: 'none',
                                color: 'var(--pt-bg-panel)',
                                boxShadow: '0 4px 12px rgba(240, 180, 41, 0.35)'
                            }}
                        >
                            {triggering ? '执行中...' : countdown > 0 ? `请等待 ${countdown} 秒` : 'Agent智能执行'}
                        </Button>
                    </Space>
                </div>
            </div>
        </Card>
    );
};

export default BotControl;
